import {
  folderTypes,
  FolderTypesNames,
} from "@redotech/redo-model/views/folders";
import { ReturnView, ViewTypesNames } from "@redotech/redo-model/views/views";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { createContext, memo } from "react";
import { CreateReturnViewFolderModal } from "../../return/views/create-return-view-folder-modal";
import { EditReturnViewFolderModal } from "../../return/views/edit-return-view-folder-modal";
import { useViewOrderingService } from "../shared/view-ordering-service-base";
import { ReloadReturnViewsContext, ReturnViewsContext } from "./return-views";

export type ReturnNotificationCounts = Record<string, number>;

export interface ReturnViewOrderingData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: ReturnView[],
    updatedFolders: folderTypes[FolderTypesNames.RETURNS][],
  ) => void;
  saveOrdering: () => void;
  toggleViewNotifications: (viewId: string) => Promise<void>;
  views: ReturnView[];
  folders: folderTypes[FolderTypesNames.RETURNS][];
  notificationCounts: ReturnNotificationCounts;
}

export const ReturnViewOrderingContext = createContext<
  ReturnViewOrderingData | undefined
>(undefined);

export const ReturnViewOrderingService = memo(
  function ReturnViewOrderingService({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const {
      views,
      folders,
      notificationCounts,
      showCreateFolderModal,
      showEditFolderModal,
      selectedFolder,
      reservedFolderNames,
      handleCreateFolder,
      handleEditFolder,
      handleDeleteFolder,
      updateOrdering,
      saveOrdering,
      toggleViewNotifications,
      openCreateFolderModal,
      openEditFolderModal,
      setShowCreateFolderModal,
      setShowEditFolderModal,
    } = useViewOrderingService({
      returnType: ReturnTypeEnum.RETURN,
      viewType: ViewTypesNames.RETURNS,
      navDataContext: ReturnViewsContext,
      reloadNavDataContext: ReloadReturnViewsContext,
    });

    return (
      <ReturnViewOrderingContext.Provider
        value={{
          openCreateFolderModal,
          openEditFolderModal,
          handleDeleteFolder,
          updateOrdering,
          saveOrdering,
          toggleViewNotifications,
          views,
          folders,
          notificationCounts,
        }}
      >
        {children}
        <CreateReturnViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditReturnViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </ReturnViewOrderingContext.Provider>
    );
  },
);
